import React from "react";
import {
  Drawer,
  Box,
  Typography,
  DrawerProps,
  SxProps,
  Theme,
  IconButton,
  Grid,
} from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface CustomDrawerProps extends DrawerProps {
  drawerTitle: string;
  drawerSubtitle?: string;
  PaperProps?: DrawerProps["PaperProps"];
  ModalProps?: DrawerProps["ModalProps"];
  BoxProps?: SxProps<Theme>;
  showCloseButton?: boolean;
  onClose: () => void;
}

const CustomDrawer: React.FC<CustomDrawerProps> = ({
  drawerTitle,
  drawerSubtitle,
  PaperProps,
  ModalProps,
  BoxProps,
  showCloseButton = true,
  onClose,
  children,
  ...drawerProps
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const defaultNavbarHeight = 64; // Default height if not provided

  const defaultPaperProps = {
    sx: {
      marginTop: `${defaultNavbarHeight}px`,
      height: `calc(100% - ${defaultNavbarHeight}px)`,
      backgroundColor: theme.palette.primary.light,
      boxShadow: theme.shadows[5],
      borderRadius: "0 0 0 8px",
      width: isMobile ? "100%" : "30%",
    },
  };

  const defaultModalProps = {
    BackdropProps: {
      sx: {
        backgroundColor: "transparent",
      },
    },
  };

  const defaultBoxProps = {
    width: isMobile ? "100%" : "50%",
  };

  return (
    <Drawer
      anchor="right"
      variant={isMobile ? "temporary" : "persistent"}
      PaperProps={{ ...defaultPaperProps, ...PaperProps }}
      ModalProps={{ ...defaultModalProps, ...ModalProps }}
      onClose={onClose}
      {...drawerProps}
    >
      <Box
        sx={{ ...defaultBoxProps, ...BoxProps, width: "100%" }}
        role="presentation"
      >
        <Grid container direction="column" sx={{ height: "100%" }}>
          {/* Close Button Section */}
          <Grid item>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                padding: theme.spacing(2),
              }}
            >
              {showCloseButton && (
                <IconButton
                  onClick={onClose}
                  sx={{ color: theme.palette.primary.main }}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          </Grid>

          {/* Title and Subtitle Section */}
          <Grid item>
            <Box
              sx={{
                textAlign: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.primary.dark,
                  letterSpacing: "1px",
                }}
              >
                {drawerTitle}
              </Typography>
              {drawerSubtitle && (
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: theme.palette.primary.light,
                    letterSpacing: "0.5px",
                  }}
                >
                  {drawerSubtitle}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs>
            <Box
              sx={{
                flexGrow: 1,
                overflow: "auto",
                padding: theme.spacing(2),
              }}
            >
              {children}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Drawer>
  );
};

export default CustomDrawer;
